<template>
  <div class="iq-answer-entity-row">
    <a
      v-if="href"
      :href="href"
      class="iq-answer-entity-row__label iq-answer-entity-row__label--link"
    >
      {{ label }}
    </a>
    <span v-else class="iq-answer-entity-row__label">{{ label }}</span>

    <div class="iq-answer-entity-row__metric">
      <div
        v-if="metricType === 'progress'"
        class="iq-answer-entity-row__meter"
        role="img"
        :aria-label="`${label}: ${normalizedValue}%`"
      >
        <div class="iq-answer-entity-row__track">
          <div
            class="iq-answer-entity-row__fill"
            :style="{ width: fillWidth }"
          ></div>
        </div>
      </div>
      <span class="iq-answer-entity-row__metric-value">{{ metricText }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  /** Row label — an employee or position name. */
  label: {
    type: String,
    required: true,
  },
  /** Same-tab link to the employee / position page. */
  href: {
    type: String,
    default: "",
  },
  /** Metric kind. "progress" renders a bar + %; "count" renders "N employees". */
  metricType: {
    type: String,
    default: "progress",
    validator: (value) => ["progress", "count"].includes(value),
  },
  metricValue: {
    type: Number,
    default: 0,
  },
});

const normalizedValue = computed(() => {
  const value = Number(props.metricValue);
  return Number.isFinite(value) ? Math.round(value) : 0;
});

const fillWidth = computed(() => {
  const clamped = Math.min(100, Math.max(0, normalizedValue.value));
  return `${clamped}%`;
});

const metricText = computed(() => {
  if (props.metricType === "count") {
    return `${normalizedValue.value} employees`;
  }
  return `${normalizedValue.value}%`;
});
</script>

<style scoped>
.iq-answer-entity-row {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 5px;
  box-sizing: border-box;
}

.iq-answer-entity-row__label {
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #101828;
  word-break: break-word;
  overflow-wrap: break-word;
}

.iq-answer-entity-row__label--link {
  color: #6941c6;
  text-decoration: none;
}

.iq-answer-entity-row__label--link:hover,
.iq-answer-entity-row__label--link:focus-visible {
  text-decoration: underline;
}

.iq-answer-entity-row__label--link:focus-visible {
  outline: 2px solid #6941c6;
  outline-offset: 2px;
  border-radius: 4px;
}

.iq-answer-entity-row__metric {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
}

.iq-answer-entity-row__meter {
  flex-shrink: 0;
  width: 168px;
}

.iq-answer-entity-row__track {
  height: 20px;
  background: #eaecf0;
  border-radius: 4px;
  overflow: hidden;
}

.iq-answer-entity-row__fill {
  height: 100%;
  background: #6941c6;
  border-radius: 4px;
}

.iq-answer-entity-row__metric-value {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
  white-space: nowrap;
}
</style>
